/**
 * Corporate Grind - Slider Free Scroll Enhancement v1.0
 * =====================================================
 * Enables smooth, delay-free scrolling on slider components:
 * - Immediate button response
 * - Hold-to-scroll (continuous scrolling while button is held)
 * - Free drag/swipe scrolling
 */

(function() {
  'use strict';
  
  console.log('🎢 CG Slider Free Scroll v1.0 - Initializing...');
  
  /**
   * Enhanced slider with free scroll support
   */
  function enhanceSliders() {
    const sliderComponents = document.querySelectorAll('slider-component, .slider-component');
    
    sliderComponents.forEach(component => {
      if (component.hasAttribute('data-cg-enhanced')) return;
      component.setAttribute('data-cg-enhanced', 'true');
      
      const slider = component.querySelector('[id^="Slider-"], .slider');
      const prevButton = component.querySelector('button[name="previous"], .slider-button--prev');
      const nextButton = component.querySelector('button[name="next"], .slider-button--next');
      
      if (!slider) return;
      
      // Enable CSS scroll snap with free scrolling
      slider.style.scrollSnapType = 'x proximity';
      slider.style.scrollBehavior = 'smooth';
      slider.style.overflowX = 'auto';
      slider.style.WebkitOverflowScrolling = 'touch';
      
      // Add touch-friendly scrolling
      enableTouchScroll(slider);
      
      // Enhance buttons with hold-to-scroll
      if (prevButton) {
        enableHoldToScroll(prevButton, slider, -1);
      }
      if (nextButton) {
        enableHoldToScroll(nextButton, slider, 1);
      }
    });
    
    console.log(`✅ Enhanced ${sliderComponents.length} slider(s)`);
  }
  
  /**
   * Enable hold-to-scroll on a button
   * @param {HTMLElement} button - The scroll button
   * @param {HTMLElement} slider - The slider container
   * @param {number} direction - 1 for next, -1 for prev
   */
  function enableHoldToScroll(button, slider, direction) {
    let scrollInterval = null;
    let isHolding = false;
    const SCROLL_SPEED = 8; // pixels per frame
    const INITIAL_DELAY = 150; // ms before continuous scroll starts
    
    const startScrolling = (e) => {
      e.preventDefault();
      isHolding = true;
      
      // Immediate first scroll
      doScroll();
      
      // Start continuous scrolling after short delay
      setTimeout(() => {
        if (isHolding) {
          scrollInterval = setInterval(doScroll, 16); // ~60fps
        }
      }, INITIAL_DELAY);
    };
    
    const stopScrolling = () => {
      isHolding = false;
      if (scrollInterval) {
        clearInterval(scrollInterval);
        scrollInterval = null;
      }
    };
    
    const doScroll = () => {
      slider.scrollBy({
        left: direction * SCROLL_SPEED,
        behavior: 'auto' // Use 'auto' for continuous scroll, 'smooth' for single clicks
      });
    };
    
    // Mouse events
    button.addEventListener('mousedown', startScrolling);
    button.addEventListener('mouseup', stopScrolling);
    button.addEventListener('mouseleave', stopScrolling);
    
    // Touch events
    button.addEventListener('touchstart', startScrolling, { passive: false });
    button.addEventListener('touchend', stopScrolling);
    button.addEventListener('touchcancel', stopScrolling);
    
    // Single click still works (fires quick scroll)
    button.addEventListener('click', (e) => {
      e.preventDefault();
      // Calculate one "page" worth of scroll
      const slideWidth = slider.querySelector('[id^="Slide-"], .slider__slide')?.offsetWidth || 300;
      slider.scrollBy({
        left: direction * slideWidth,
        behavior: 'smooth'
      });
    });
  }
  
  /**
   * Enable touch/mouse drag scrolling
   * @param {HTMLElement} slider - The slider container
   */
  function enableTouchScroll(slider) {
    let isDown = false;
    let startX;
    let scrollLeft;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;
    let momentumId = null;
    
    const handleStart = (e) => {
      isDown = true;
      slider.classList.add('cg-dragging');
      startX = (e.pageX || e.touches?.[0]?.pageX) - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
      lastX = startX;
      lastTime = Date.now();
      velocity = 0;
      
      // Stop any ongoing momentum scroll
      if (momentumId) {
        cancelAnimationFrame(momentumId);
        momentumId = null;
      }
    };
    
    const handleEnd = () => {
      if (!isDown) return;
      isDown = false;
      slider.classList.remove('cg-dragging');
      
      // Apply momentum scrolling
      if (Math.abs(velocity) > 0.5) {
        applyMomentum();
      }
    };
    
    const handleMove = (e) => {
      if (!isDown) return;
      
      const x = (e.pageX || e.touches?.[0]?.pageX) - slider.offsetLeft;
      const walk = (x - startX) * 1.5; // Scroll speed multiplier
      slider.scrollLeft = scrollLeft - walk;
      
      // Calculate velocity for momentum
      const now = Date.now();
      const dt = now - lastTime;
      if (dt > 0) {
        velocity = (x - lastX) / dt;
      }
      lastX = x;
      lastTime = now;
    };
    
    const applyMomentum = () => {
      const friction = 0.95;
      const minVelocity = 0.1;
      
      const step = () => {
        if (Math.abs(velocity) < minVelocity) {
          momentumId = null;
          return;
        }
        
        slider.scrollLeft -= velocity * 16;
        velocity *= friction;
        momentumId = requestAnimationFrame(step);
      };
      
      step();
    };
    
    // Mouse events
    slider.addEventListener('mousedown', handleStart);
    slider.addEventListener('mouseleave', handleEnd);
    slider.addEventListener('mouseup', handleEnd);
    slider.addEventListener('mousemove', handleMove);
    
    // Touch events (passive false to allow preventDefault if needed)
    slider.addEventListener('touchstart', handleStart, { passive: true });
    slider.addEventListener('touchend', handleEnd, { passive: true });
    slider.addEventListener('touchmove', handleMove, { passive: true });
  }
  
  // Add CSS for dragging state
  function addStyles() {
    if (document.getElementById('cg-slider-free-scroll-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'cg-slider-free-scroll-styles';
    style.textContent = `
      /* Free scroll slider enhancements */
      [id^="Slider-"], .slider {
        scroll-snap-type: x proximity !important;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        cursor: grab;
      }
      
      [id^="Slider-"]:active, .slider:active,
      [id^="Slider-"].cg-dragging, .slider.cg-dragging {
        cursor: grabbing;
        scroll-snap-type: none !important;
        scroll-behavior: auto !important;
      }
      
      /* Slider buttons - no delay */
      button[name="previous"], button[name="next"],
      .slider-button--prev, .slider-button--next {
        user-select: none;
        -webkit-user-select: none;
        touch-action: manipulation;
      }
      
      /* Hide scrollbar but keep functionality */
      [id^="Slider-"]::-webkit-scrollbar,
      .slider::-webkit-scrollbar {
        height: 0;
        display: none;
      }
      
      /* Ensure slides don't interfere with dragging */
      [id^="Slide-"], .slider__slide {
        pointer-events: auto;
      }
      
      .cg-dragging [id^="Slide-"], .cg-dragging .slider__slide {
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);
  }
  
  // Initialize
  function init() {
    addStyles();
    enhanceSliders();
    
    // Re-enhance on dynamic content changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.addedNodes.length) {
          enhanceSliders();
        }
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  console.log('✅ CG Slider Free Scroll v1.0 - Ready');
  
})();
